import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import * as RPopover from '@radix-ui/react-popover';
import { Search, Loader2 } from 'lucide-react';
import { Avatar, Button, useLoad, fmtDate } from '../kit';
import { readRecent, nameOf, isLive, isMine, daysBetween, agoText } from './participants';

// Choose who the time is for: a searchable list with your own participants first, then the ones you
// logged time for recently, then everyone else. A combobox (type to filter, arrows to move, Enter to
// choose). The list is a Radix popover so Escape closes the list, not the sheet it sits in.
//
// value: { id, name } | null.  onChange({ id, name, row }).  me: { userId, firstName, lastName }.
const MAX_ROWS = 80;

export default function ParticipantPicker({ id, value, onChange, me, today, autoFocus, 'aria-invalid': invalid }) {
  const list = useLoad('/support-coordination');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const uid = useId().replace(/:/g, '');
  const listId = `${uid}-list`;
  const optId = (rowId) => `${uid}-opt-${rowId}`;
  const userId = me && me.userId;
  const recent = useMemo(() => readRecent(userId), [userId]);

  const groups = useMemo(() => {
    const rows = (list.data || []).filter(isLive);
    const q = query.trim().toLowerCase();
    const match = (r) => !q || [nameOf(r), r.name, r.ndis_number].filter(Boolean).join(' ').toLowerCase().includes(q);
    const rank = new Map(recent.map((rid, i) => [rid, i]));
    const byName = (a, b) => nameOf(a).localeCompare(nameOf(b));
    const byRecent = (a, b) => (rank.has(a.id) ? rank.get(a.id) : 99) - (rank.has(b.id) ? rank.get(b.id) : 99) || byName(a, b);
    const mineAll = rows.filter((r) => isMine(r, me));
    const mineIds = new Set(mineAll.map((r) => r.id));
    const recentRows = rows.filter((r) => !mineIds.has(r.id) && rank.has(r.id));
    const recentIds = new Set(recentRows.map((r) => r.id));
    const rest = rows.filter((r) => !mineIds.has(r.id) && !recentIds.has(r.id));
    // Two engagements for one person read the same by name: their plan dates tell them apart.
    const counts = rows.reduce((m, r) => m.set(nameOf(r), (m.get(nameOf(r)) || 0) + 1), new Map());
    const out = [
      { key: 'mine', label: 'Your participants', rows: mineAll.filter(match).sort(byRecent) },
      { key: 'recent', label: 'Logged recently', rows: recentRows.filter(match).sort(byRecent) },
      { key: 'rest', label: mineAll.length || recentRows.length ? 'Other participants' : 'Participants', rows: rest.filter(match).sort(byName) },
    ].filter((g) => g.rows.length);
    let budget = MAX_ROWS;
    return {
      dupes: counts,
      groups: out.map((g) => { const rowsShown = g.rows.slice(0, Math.max(0, budget)); budget -= rowsShown.length; return { ...g, rows: rowsShown }; }).filter((g) => g.rows.length),
      clipped: out.reduce((t, g) => t + g.rows.length, 0) > MAX_ROWS,
    };
  }, [list.data, query, recent, me]);
  const flat = useMemo(() => groups.groups.flatMap((g) => g.rows), [groups]);

  useEffect(() => { setActive(0); }, [query]);
  // Keep the highlighted option in view while arrowing through a long list.
  const activeId = open && flat[active] ? `${uid}-opt-${flat[active].id}` : null;
  useEffect(() => {
    const el = activeId ? document.getElementById(activeId) : null;
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const choose = (r) => {
    onChange({ id: r.id, name: nameOf(r), row: r });
    setOpen(false);
    setQuery('');
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Home' && open) { e.preventDefault(); setActive(0); }
    else if (e.key === 'End' && open) { e.preventDefault(); setActive(Math.max(0, flat.length - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && flat[active]) choose(flat[active]); else setOpen(true); }
  };

  const detail = (r) => {
    const days = r.last_note ? daysBetween(r.last_note, today) : null;
    const bits = [r.last_note ? `Last note ${agoText(days)}` : 'No notes yet'];
    if ((groups.dupes.get(nameOf(r)) || 0) > 1 && (r.plan_start || r.plan_end)) bits.push(`plan ${fmtDate(r.plan_start)} to ${fmtDate(r.plan_end)}`);
    return bits.join(' · ');
  };

  let idx = -1;
  return (
    <RPopover.Root open={open} onOpenChange={setOpen}>
      <RPopover.Anchor asChild>
        <div ref={anchorRef} className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" aria-hidden />
          <input
            ref={inputRef} id={id} type="text" role="combobox" autoComplete="off" autoFocus={autoFocus}
            aria-expanded={open} aria-controls={listId} aria-autocomplete="list" aria-invalid={invalid}
            aria-activedescendant={activeId || undefined}
            value={open ? query : (value ? value.name : '')}
            placeholder={value ? value.name : 'Search your participants'}
            onFocus={() => { setQuery(''); setOpen(true); }}
            onClick={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
            onKeyDown={onKeyDown}
            className={clsx('w-full h-11 sm:h-10 rounded-xl border-0 bg-slate-50 ring-1 ring-inset ring-slate-200 pl-9 pr-3 text-[13.5px] text-slate-900',
              'placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white aria-[invalid=true]:ring-rose-400',
              value && !open && 'font-semibold')} />
        </div>
      </RPopover.Anchor>
      <RPopover.Portal>
        <RPopover.Content align="start" sideOffset={4} collisionPadding={12} role="presentation"
          onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => { if (anchorRef.current && anchorRef.current.contains(e.target)) e.preventDefault(); }}
          className="z-[90] w-[var(--radix-popover-trigger-width)] max-h-[min(var(--radix-popover-content-available-height),340px)] overflow-y-auto overscroll-contain bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1 data-[state=open]:animate-fade-in">
          <div role="listbox" id={listId} aria-label="Participants">
            {list.loading && !list.data ? (
              <p className="flex items-center gap-2 px-3 py-3 text-[13px] text-slate-600"><Loader2 size={14} className="animate-spin" aria-hidden /> Loading participants…</p>
            ) : list.error ? (
              <div className="px-3 py-3 text-[13px] text-slate-700 space-y-2">
                <p>Couldn’t load your participants. Check your connection, then try again.</p>
                <Button size="sm" variant="secondary" onMouseDown={(e) => e.preventDefault()} onClick={list.reload}>Try again</Button>
              </div>
            ) : !flat.length ? (
              <p className="px-3 py-3 text-[13px] text-slate-600">{query.trim() ? `No participant matches “${query.trim()}”.` : 'No active participants yet. Add one from Caseload.'}</p>
            ) : groups.groups.map((g) => (
              <div key={g.key} role="group" aria-labelledby={`${uid}-${g.key}`}>
                <p id={`${uid}-${g.key}`} className="px-3 pt-2 pb-1 text-[12px] font-semibold text-slate-500">{g.label}</p>
                {g.rows.map((r) => {
                  idx += 1;
                  const i = idx;
                  const on = i === active;
                  const selected = value && value.id === r.id;
                  return (
                    <div key={r.id} id={optId(r.id)} role="option" aria-selected={on}
                      onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => choose(r)}
                      className={clsx('flex items-center gap-2.5 px-2.5 min-h-[44px] py-1.5 rounded-lg cursor-pointer select-none', on ? 'bg-indigo-50' : 'hover:bg-slate-50')}>
                      <Avatar first={r.first_name} last={r.last_name} size={28} />
                      <span className="min-w-0 flex-1">
                        <span className={clsx('block text-[13.5px] truncate', on ? 'text-indigo-900' : 'text-slate-900', selected ? 'font-bold' : 'font-semibold')}>{nameOf(r)}</span>
                        <span className="block text-[12px] text-slate-500 truncate">{detail(r)}</span>
                      </span>
                      {selected && <span className="text-[12px] font-semibold text-indigo-700">Chosen</span>}
                    </div>
                  );
                })}
              </div>
            ))}
            {groups.clipped && <p className="px-3 py-2 text-[12px] text-slate-500">Type a name to find someone further down the list.</p>}
          </div>
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}
